import type { TippyV2Props } from "@circle-react-shared/TippyV2";
import { TippyV2 } from "@circle-react-shared/TippyV2";
import { Tooltip as CompassTooltip } from "@circleco/compass/components/Tooltip";
import { allowedClassesFor } from "@circleco/compass/helpers/allowedClassesFor";
import { isValidElement } from "react";

import { classNames } from "@/react/helpers/twMergeWithCN";
import { useIsCircleV4FeatureEnabled } from "@/react/hooks/useIsCircleV4FeatureEnabled";

// eslint-disable-next-line no-barrel-files/no-barrel-files -- Re-export for compatibility
export type { TippyV2Props } from "@circle-react-shared/TippyV2";

type CompassSide = "top" | "right" | "bottom" | "left";
const mapPlacementToSide = (
  placement: TippyV2Props["placement"],
): CompassSide => {
  if (!placement) return "top";

  if (placement.startsWith("top")) return "top";
  if (placement.startsWith("bottom")) return "bottom";
  if (placement.startsWith("left")) return "left";
  if (placement.startsWith("right")) return "right";
  if (placement.startsWith("auto")) return "top";

  return "top";
};

const parseDelay = (
  delay: TippyV2Props["delay"],
): { openDelay: number; closeDelay: number } => {
  if (!delay) {
    return { openDelay: 200, closeDelay: 0 };
  }

  if (typeof delay === "number") {
    return { openDelay: delay, closeDelay: delay };
  }

  if (Array.isArray(delay)) {
    const [show, hide] = delay;
    return {
      openDelay: show ?? 200,
      closeDelay: hide ?? 0,
    };
  }

  return { openDelay: 200, closeDelay: 0 };
};

export const Tooltip = (props: TippyV2Props) => {
  const isCompassTooltipEnabled = useIsCircleV4FeatureEnabled(
    "circle_v4_tooltip_enabled",
  );

  if (isCompassTooltipEnabled) {
    const {
      children,
      content,
      placement,
      delay,
      enabled: isEnabled = true,
      disabled: isDisabled,
      className,
      tooltipTextClassName,
      shouldUseSpanWrapper = true,
      dataTestId,
    } = props;

    const shouldDisable = !isEnabled || isDisabled || !content;
    const side = mapPlacementToSide(placement);
    const { openDelay, closeDelay } = parseDelay(delay);

    // Compass Tooltip uses cloneElement to inject a ref into the trigger child for
    // positioning. React component elements (non-DOM) may not forward refs, which
    // silently breaks the tooltip. Wrapping in a span ensures the ref always lands
    // on a real DOM node.
    const shouldWrapForRef =
      isValidElement(children) && typeof children.type !== "string";
    const shouldWrapTrigger = shouldUseSpanWrapper || shouldWrapForRef;
    const triggerChild = shouldWrapTrigger ? (
      <span className={classNames(className)} data-testid={dataTestId}>
        {children}
      </span>
    ) : (
      children
    );

    return (
      <CompassTooltip
        content={content}
        side={side}
        delay={openDelay}
        closeDelay={closeDelay}
        disabled={shouldDisable}
        data-testid={shouldWrapTrigger ? undefined : dataTestId}
        className={allowedClassesFor("Tooltip", tooltipTextClassName ?? "")}
      >
        {triggerChild}
      </CompassTooltip>
    );
  }

  return <TippyV2 {...props} />;
};

Tooltip.displayName = "Tooltip";

// eslint-disable-next-line no-barrel-files/no-barrel-files -- Re-export for compatibility
export { Tooltip as TippyV2 };
